import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import {
  Grid,
  TextField,
  FormHelperTextProps,
  Typography,
  FormControlLabel,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './ConfigurationApi.styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { t } from 'i18next';
import { ConfigurationApiDetails, apiTypeId } from './configurationApi.types';
import { getSyncTime, getSystemTypeName } from './helpers';
import { useLayoutContext } from '../../../Context/Layout.context';
import { updateConfigurationApiDetails } from '../../../services/admin/settings/systemSettings.service';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';

const NAME_MAX_LENGTH = 20;
const DESCRIPTION_MAX_LENGTH = 256;
const HOST_NAME_MAX_LENGTH = 256;

interface EditApiSettingsProps {
  data: ConfigurationApiDetails;
  title: string;
  onCancel: () => void;
  onClose: () => void;
}

export const EditApiSettings: React.FC<EditApiSettingsProps> = ({ data, title, onCancel, onClose }) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();

  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [systemName, setSystemName] = useState<string>(data.name ?? '');
  const [systemDescription, setSystemDescription] = useState<string>(data.description ?? '');
  const [hostName, setHostName] = useState<string>(data.servicesHost ?? '');
  const [hasNameFocus, setHasNameFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [hasHostNameFocus, setHasHostNameFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [systemType, setSystemType] = useState<apiTypeId>(data.apiTypeId ?? apiTypeId.SIOEO);
  const [userName, setUserName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [secretNameFirstValue, setSecretNameFirstValue] = useState<string>('');
  const [secretNameSecondValue, setSecretNameSecondValue] = useState<string>('');

  const isSystemNameToLong = systemName.length > NAME_MAX_LENGTH;
  const isSystemDescriptionToLong = systemDescription.length > DESCRIPTION_MAX_LENGTH;
  const isHostNameToLong = hostName.length > HOST_NAME_MAX_LENGTH;

  useEffect(() => {
    switch (data.apiTypeId) {
      case apiTypeId.SIOEO:
        {
          setSecretNameFirstValue(data.secretNameFirstValue ?? '');
          setSecretNameSecondValue(data.secretNameSecondValue ?? '');
        }
        break;
      case apiTypeId.SIOEPKZ:
        {
          setUserName(data.secretNameFirstValue ?? '');
          setPassword(data.secretNameSecondValue ?? '');
        }
        break;
    }
  }, [data.apiTypeId]);

  const onSave = () => {
    showLoader(true);
    updateConfigurationApiDetails(
      data.configurationApiId,
      systemName,
      systemDescription,
      hostName,
      systemType === apiTypeId.SIOEO ? secretNameFirstValue : userName,
      systemType === apiTypeId.SIOEO ? secretNameSecondValue : password,
      systemType,
    )
      .then(
        (result: ConfigurationApiDetails) => {
          openAlert(
            'success',
            `Zapisano nową konfigurację. Zmiany zostaną wprowadzone w ciagu ${getSyncTime(result.syncTimeMinutes)}`,
          );
        },
        () => {
          openAlert('save_error');
        },
      )
      .finally(() => {
        showLoader(false);
        onClose();
      });
  };

  const handleSystemNameChange = (value: string) => {
    setSystemName(value);
  };

  const handleSystemDecriptionChange = (value: string) => {
    setSystemDescription(value);
  };

  const handleHostNameChange = (value: string) => {
    setHostName(value);
  };

  const handleSecretNameFirstValueChange = (value: string) => {
    setSecretNameFirstValue(value);
  };

  const handleSecretNameSecondValueChange = (value: string) => {
    setSecretNameSecondValue(value);
  };

  const handleUserNameChange = (value: string) => {
    setUserName(value);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
  };

  const handleClickShowNewPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    setIsDataChanged(
      (systemName !== '' && systemName !== data.name) ||
        (systemDescription !== '' && systemDescription !== data.description) ||
        (hostName !== '' && hostName !== data.servicesHost) ||
        (secretNameFirstValue !== '' && secretNameFirstValue !== data.secretNameFirstValue) ||
        (secretNameSecondValue !== '' && secretNameSecondValue !== data.secretNameSecondValue) ||
        (userName !== '' && userName !== data.secretNameFirstValue) ||
        (password !== '' && password !== data.secretNameSecondValue),
    );
  }, [
    systemName,
    systemDescription,
    hostName,
    systemType,
    secretNameFirstValue,
    secretNameSecondValue,
    userName,
    password,
  ]);

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  const typesStyle = {
    '&.Mui-checked': {
      color: '#3C419A',
    },
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="sm"
        title={title}
        autoHeight
        onCancel={handleCancel}
        confirmButtonLabel="Zapisz"
        onConfirm={onSave}
        confirmButtonDisabled={
          !systemName || !systemDescription || isSystemNameToLong || isSystemDescriptionToLong || !isDataChanged
        }
        buttonsContainerStyle={{ marginTop: '20px' }}
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Nazwa i opis systemu*</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={systemName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSystemNameChange(e.target.value)}
              label="Nazwa"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  color: isSystemNameToLong ? 'red' : 'black',
                },
              }}
              onFocus={() => setHasNameFocus(true)}
              onBlur={() => setHasNameFocus(false)}
              error={isSystemNameToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isSystemNameToLong ? 'Podana nazwa jest za długa' : ''}
                  limitsVisible={hasNameFocus || isSystemNameToLong}
                  valueLength={systemName.length}
                  limit={NAME_MAX_LENGTH}
                  withError={isSystemNameToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={systemDescription}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleSystemDecriptionChange(e.target.value)}
              label="Opis"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isSystemDescriptionToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isSystemDescriptionToLong ? 'Podany opis jest za długi' : ''}
                  limitsVisible={hasDescriptionFocus || isSystemDescriptionToLong}
                  valueLength={systemDescription.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isSystemDescriptionToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Host usługi*</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={hostName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleHostNameChange(e.target.value.trim())}
              label="URL"
              variant="outlined"
              fullWidth
              onFocus={() => setHasHostNameFocus(true)}
              onBlur={() => setHasHostNameFocus(false)}
              error={isHostNameToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isHostNameToLong ? 'Podany adres URL jest za długi' : ''}
                  limitsVisible={hasHostNameFocus || isHostNameToLong}
                  valueLength={hostName.length}
                  limit={HOST_NAME_MAX_LENGTH}
                  withError={isHostNameToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Model API*</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <RadioGroup
              row
              aria-label="wybór typu systemu"
              name="system-type-selector"
              value={systemType}
              onChange={(e) => {
                setSystemType(+e.target.value);
              }}
            >
              <FormControlLabel
                disabled={systemType === apiTypeId.SIOEPKZ}
                value={apiTypeId.SIOEO}
                control={<Radio sx={typesStyle} />}
                label={getSystemTypeName(apiTypeId.SIOEO)}
              />
              <FormControlLabel
                disabled={systemType === apiTypeId.SIOEO}
                value={apiTypeId.SIOEPKZ}
                control={<Radio sx={typesStyle} />}
                label={getSystemTypeName(apiTypeId.SIOEPKZ)}
              />
            </RadioGroup>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            {systemType === apiTypeId.SIOEO ? (
              <>
                <Grid item xs={6} style={{ paddingRight: '10px' }}>
                  <TextField
                    value={secretNameFirstValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleSecretNameFirstValueChange(e.target.value.trim())
                    }
                    id="secret-name-first-value"
                    label="Wejściowy API Key"
                    variant="outlined"
                    fullWidth={true}
                    inputProps={{
                      style: {
                        padding: '15.5px',
                        color: '#2C2D3E',
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '10px' }}>
                  <TextField
                    value={secretNameSecondValue}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleSecretNameSecondValueChange(e.target.value.trim())
                    }
                    id="secret-name-second-value"
                    label="Wyjściowy API Key"
                    variant="outlined"
                    fullWidth={true}
                    inputProps={{
                      style: {
                        padding: '15.5px',
                        color: '#2C2D3E',
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6} style={{ paddingRight: '10px' }}>
                  <TextField
                    value={userName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleUserNameChange(e.target.value.trim())}
                    id="user-name"
                    label="Nazwa użytkownika"
                    variant="outlined"
                    fullWidth={true}
                    inputProps={{
                      style: {
                        padding: '15.5px',
                        color: '#2C2D3E',
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={6} style={{ paddingLeft: '10px' }}>
                  <FormControl variant="outlined">
                    <InputLabel htmlFor="password" shrink>
                      Hasło
                    </InputLabel>
                    <OutlinedInput
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handlePasswordChange(e.target.value.trim())}
                      fullWidth={true}
                      notched
                      label={'Hasło'}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            disableRipple
                            aria-label="włącz widoczność hasła"
                            onClick={handleClickShowNewPassword}
                            edge="end"
                          >
                            {!showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                      inputProps={{
                        style: {
                          padding: '15.5px',
                          color: '#2C2D3E',
                        },
                      }}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
